import { API_BASE_URL } from '../config/api';
import { DiagnosticCatalogItem } from './labsCatalogApi';

function getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token');
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
    };
}

export interface LabOrder {
    id: string;
    pet_id: string;
    diagnostic_id: string;
    encounter_id?: string;
    encounter_date?: string;
    quantity: number;
    comments?: string;
    performed_at?: string;
    result?: string;
    result_notes?: string;
    result_attachment_s3_key?: string;
    status: 'PENDING' | 'COMPLETED' | 'CANCELLED';
    created_at: string;
    updated_at: string;
    diagnostic?: DiagnosticCatalogItem;
}

export const getPetLabs = async (petId: string): Promise<LabOrder[]> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/pets/${petId}/labs`, {
        headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to fetch lab orders');
    return response.json();
};

export const createLabOrder = async (petId: string, data: Partial<LabOrder>): Promise<LabOrder> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/pets/${petId}/labs`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
    });

    if (!response.ok) throw new Error('Failed to create lab order');
    return response.json();
};

export const updateLabOrder = async (orderId: string, data: Partial<LabOrder>): Promise<LabOrder> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/labs/${orderId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
    });

    if (!response.ok) throw new Error('Failed to update lab order');
    return response.json();
};

export const deleteLabOrder = async (orderId: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/labs/${orderId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to delete lab order');
};

// ===== LAB ATTACHMENT FUNCTIONS =====

export const initiateLabAttachmentUpload = async (labOrderId: string, contentType: string = 'application/pdf'): Promise<{ upload_url: string; fields: Record<string, string>; s3_key: string }> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/labs/${labOrderId}/attachment/initiate?content_type=${encodeURIComponent(contentType)}`, {
        method: 'POST',
        headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to initiate attachment upload');
    return response.json();
};

export const completeLabAttachmentUpload = async (labOrderId: string, s3Key: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/labs/${labOrderId}/attachment/complete?s3_key=${encodeURIComponent(s3Key)}`, {
        method: 'POST',
        headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to complete attachment upload');
};

export const getLabAttachmentDownloadUrl = async (labOrderId: string): Promise<string> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/labs/${labOrderId}/attachment/download`, {
        headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to get attachment download URL');
    const data = await response.json();
    return data.download_url;
};

export const deleteLabAttachment = async (labOrderId: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/labs/${labOrderId}/attachment`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to delete attachment');
};
